import { useState } from 'react';
import { Banknote } from 'lucide-react';
import Modal from './Modal';
import { formatCurrency } from '../lib/utils';
import type { InvoiceMilestone } from '../types';

interface Props {
  open: boolean;
  projectName: string;
  projectValue: number;
  paidSoFar: number;
  projectId: string;
  onSave: (inv: Omit<InvoiceMilestone, 'id'>) => void;
  onClose: () => void;
}

export default function QuickPayModal({ open, projectName, projectValue, paidSoFar, projectId, onSave, onClose }: Props) {
  const remaining = Math.max(0, projectValue - paidSoFar);
  const today = new Date().toISOString().split('T')[0];
  const [amount, setAmount] = useState<number>(0);
  const [date, setDate] = useState(today);
  const [notes, setNotes] = useState('');

  const reset = () => { setAmount(0); setDate(today); setNotes(''); };
  const handleClose = () => { reset(); onClose(); };

  const handleSave = () => {
    if (!amount) return;
    onSave({
      projectId,
      relatedStageId: null,
      title: 'Makse laekunud',
      amount,
      vatRate: 0,
      percentage: 0,
      plannedSendDate: date,
      sentDate: date,
      paidDate: date,
      status: 'paid',
      notes,
    });
    reset();
  };

  const inputCls = 'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500';

  return (
    <Modal open={open} onClose={handleClose} title="Record Payment">
      <div className="space-y-4">
        <div className="bg-gray-50 rounded-lg p-3 text-sm">
          <p className="font-medium text-gray-800">{projectName}</p>
          <div className="flex gap-4 mt-1 text-xs text-gray-500">
            <span>Total project value: <strong className="text-gray-700">{formatCurrency(projectValue)}</strong></span>
            <span>Paid so far: <strong className="text-green-600">{formatCurrency(paidSoFar)}</strong></span>
            {remaining > 0 && <span>Remaining: <strong className="text-gray-700">{formatCurrency(remaining)}</strong></span>}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Amount received (€) *</label>
            <input
              type="number"
              value={amount || ''}
              onChange={(e) => setAmount(Number(e.target.value))}
              className={inputCls}
              placeholder={remaining > 0 ? String(remaining) : '0'}
              autoFocus
            />
            {remaining > 0 && (
              <button
                onClick={() => setAmount(remaining)}
                className="text-xs text-brand-500 hover:text-brand-600 mt-1"
              >
                Use remaining ({formatCurrency(remaining)})
              </button>
            )}
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Date received</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
          </div>
        </div>

        <div>
          <label className="text-xs text-gray-500 block mb-1">Notes (optional)</label>
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className={inputCls}
            placeholder="e.g. bank transfer ref. 12345"
          />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={handleClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!amount}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50"
          >
            <Banknote size={14} /> Mark Paid
          </button>
        </div>
      </div>
    </Modal>
  );
}
